import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as songsApi from "../../api/songsApi";
import * as catalogApi from "../../api/catalogApi";
import { failureMessage } from "../../api/client";
import { addSongToLibrary } from "../../actions/libraryActions";
import { deleteSong, songUpdated } from "../../actions/catalogActions";
import { showInfo } from "../../actions/noticeActions";
import { KEYS, versesToText } from "../../lib/songText";
import { parseSections } from "../../lib/sections";

const EXAMPLE = `[Verse 1]
Amazing [G]grace, how [C]sweet the [G]sound

[Chorus]
G            C
Chords above the words line up by column`;

const errorMessage = (error) => failureMessage(error, "Could not save the song, please try again");

const emptyForm = { title: "", artist: "", key: "", capo: "", lyrics: "", public: false };

/**
 * Creates a song (/songs/new) or edits one (/songs/:id/edit). Hosts only. Inside the
 * host's sheet (`inSheet`) it saves and hands the song back instead of navigating.
 */
const SongEditorPage = ({ inSheet = false, draft = null, onSaved, onCancel }) => {
  const { id } = useParams();
  const songId = inSheet ? null : id ? Number(id) : null;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const canPublish = useSelector((store) => store.host.canPublish);

  // a song imported from a link arrives here when the host wants to edit it first
  const [form, setForm] = useState(() => {
    const initial = draft || (!songId && state?.draft) || null;
    return initial ? { ...emptyForm, ...initial } : emptyForm;
  });
  const [loading, setLoading] = useState(Boolean(songId));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [duplicate, setDuplicate] = useState(null);

  useEffect(() => {
    if (!songId) return undefined;
    let cancelled = false;
    songsApi
      .getSong(songId)
      .then((song) => {
        if (cancelled) return;
        setForm({
          title: song.Title || "",
          artist: song.Artist || "",
          key: song.Key || "",
          capo: song.Capo ? String(song.Capo) : "",
          lyrics: versesToText(song.Verses),
          public: song.OwnerId == null,
        });
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) navigate("/songs", { replace: true });
      });
    return () => {
      cancelled = true;
    };
  }, [songId, navigate]);

  const update = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  // Warn about an existing song with the same title (new songs only); saving stays possible.
  const checkDuplicate = async () => {
    const title = form.title.trim();
    if (songId || !title) return;
    try {
      const matches = await catalogApi.search(title);
      setDuplicate(matches.find((song) => song.Title.trim().toLowerCase() === title.toLowerCase()) || null);
    } catch {
      setDuplicate(null);
    }
  };

  const sections = parseSections(form.lyrics);
  const chordTotal = sections.reduce((total, section) => total + section.chordCount, 0);
  const canSave = !saving && !loading && form.title.trim().length > 0 && sections.length > 0;

  const keyIndex = KEYS.indexOf(form.key);
  const stepKey = (delta) => {
    const next = keyIndex < 0 ? (delta > 0 ? 0 : KEYS.length - 1) : (keyIndex + delta + KEYS.length) % KEYS.length;
    setForm((current) => ({ ...current, key: KEYS[next] }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!canSave) return;
    setSaving(true);
    setError(null);
    const song = { ...form, title: form.title.trim(), artist: form.artist.trim() };
    try {
      if (songId) {
        const updated = await songsApi.updateGuitarSong(songId, song);
        dispatch(songUpdated(updated));
        dispatch(showInfo("Changes saved", { sub: updated.Title }));
        navigate(-1);
      } else {
        const created = await songsApi.addGuitarSong(song);
        dispatch(addSongToLibrary(created.Id));
        if (inSheet) {
          onSaved?.(created);
          return;
        }
        dispatch(showInfo("Saved to your songs", { sub: form.public ? "Visible to everyone" : "Only you see it in the lists" }));
        navigate(`/songs/${created.Id}`, { replace: true });
      }
    } catch (err) {
      setError(errorMessage(err));
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${form.title}" for everyone? This cannot be undone.`)) return;
    setSaving(true);
    if (await dispatch(deleteSong(songId))) {
      dispatch(showInfo("Song deleted", { sub: "Removed from every set" }));
      navigate("/songs", { replace: true });
    } else setSaving(false);
  };

  const cancel = () => (inSheet ? onCancel?.() : navigate(-1));

  if (loading) {
    return <div className="page loading">Loading…</div>;
  }

  const heading = songId ? "Edit song" : "Create a song";
  const countLabel = sections.length ? `${sections.length} ${sections.length === 1 ? "section" : "sections"} · ${chordTotal} chords` : "";
  const saveLabel = saving ? "Saving…" : songId ? "Save changes" : duplicate ? "Save anyway" : inSheet ? "Save and add to the set" : "Save song";

  return (
    <form className={inSheet ? "stack" : "page"} style={inSheet ? { flex: 1, minHeight: 0, gap: 8 } : undefined} onSubmit={submit} data-screen-label={inSheet ? "Host sheet · editor" : "Song editor"}>
      {inSheet ? (
        <div className="sheet-head" style={{ marginBottom: 4 }}>
          <button type="button" className="btn btn-round btn-xs btn-outline" onClick={cancel} aria-label="Back">
            ←
          </button>
          <span className="sheet-title">{heading}</span>
          <span className="sheet-pos">{countLabel}</span>
        </div>
      ) : (
        <>
          <div className="between">
            <button type="button" className="back-link" onClick={cancel} disabled={saving}>
              ← Cancel
            </button>
            <span className="sheet-pos" style={{ color: "var(--muted)" }}>
              {countLabel}
            </span>
          </div>
          <h2 className="page-title" style={{ fontSize: 26 }}>
            {heading}
          </h2>
        </>
      )}
      <input id="song-title" className="field field-serif" value={form.title} onChange={update("title")} onBlur={checkDuplicate} placeholder="Title" aria-label="Title" maxLength={100} autoComplete="off" />
      <div className="editor-meta">
        <input id="song-artist" className="field field-sm" value={form.artist} onChange={update("artist")} placeholder="Artist" aria-label="Artist" maxLength={100} autoComplete="off" />
        <div className="stepper stepper-lg" role="group" aria-label="Key">
          <button type="button" onClick={() => stepKey(-1)} aria-label="Previous key">
            −
          </button>
          <span>{form.key ? `Key ${form.key}` : "Key –"}</span>
          <button type="button" onClick={() => stepKey(1)} aria-label="Next key">
            ＋
          </button>
        </div>
        <input id="song-capo" className="field field-sm field-center capo" inputMode="numeric" value={form.capo} onChange={(event) => setForm((current) => ({ ...current, capo: event.target.value.replace(/[^0-9]/g, "").slice(0, 2) }))} placeholder="Capo" aria-label="Capo" />
      </div>
      {duplicate && (
        <p className="warn-text" role="status">
          A song called “{duplicate.Title}” already exists. Saving keeps both.{" "}
          {!inSheet && (
            <button type="button" className="link-btn" onClick={() => navigate(`/songs/${duplicate.Id}/edit`)}>
              Edit that one instead
            </button>
          )}
        </p>
      )}
      <textarea id="song-lyrics" className="field field-area field-mono editor-area" value={form.lyrics} onChange={update("lyrics")} placeholder={EXAMPLE} aria-label="Lyrics and chords" spellCheck={false} />
      {sections.length > 0 ? (
        <div className="preview-list" style={{ maxHeight: 104 }}>
          {sections.map((section) => (
            <div key={section.id} className="preview-row">
              <span className="preview-label">{section.label}</span>
              <span className="preview-text">{section.preview}</span>
              {section.chords && <span className="preview-meta">{section.chords}</span>}
            </div>
          ))}
        </div>
      ) : (
        <p className="note">
          Name a section in square brackets on its own line. Chords go inline in brackets: Amazing [G]grace, or on the line above the words. Blank lines split sections.
          {!inSheet && !songId && " Only you find this song in the lists; people in your session see it when you queue it."}
        </p>
      )}
      {canPublish && !inSheet && (
        <label className="checkbox-row">
          <input type="checkbox" checked={form.public} onChange={(event) => setForm((current) => ({ ...current, public: event.target.checked }))} />
          Visible to everyone (public list)
        </label>
      )}
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
      <div className="btn-row" style={{ flex: "none" }}>
        {songId && (
          <button type="button" className="btn btn-md btn-danger" style={{ flex: "none" }} onClick={remove} disabled={saving}>
            Delete
          </button>
        )}
        <button type="submit" className="btn btn-md btn-primary" disabled={!canSave}>
          {saveLabel}
        </button>
      </div>
    </form>
  );
};

export default SongEditorPage;
